import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppThunk } from '../store'
import { postLogin, getAccessToken, postLogout, postMasterLogin, postMasterLoginManagement, getMfaToken } from '../../apis/auth'
import { setErrors, resetFormErrors } from '../common/errorsSlice'
import { Login, LoginResponse } from '../../types/auth'
import { RolePermission } from '../../types/setting'

interface LoginState {
  current_member_id: number
  company_id: number
  accessToken: string
  isLoading: boolean
  isMasterLogin: boolean
  role: ("member" | "manager" | "master" | "administrator")[]
  role_name: string | null
  company_plan: string
  limit_member_number: number
  feature_config: {
    setting_only_master: boolean
    is_team_report_display: boolean
    attendance_alert: boolean
    password_reset: boolean
    slack_connect: boolean
    summary_view: boolean
    hide_analysis: boolean
    hide_support_chat: boolean
    external_api: boolean
    change_contract: boolean
    enable_project_member_setting: boolean
    editable_weekly_track_time: boolean
    task_report_view: boolean
    task_category_report_view: boolean
    task_approver: boolean
    task_overwrite_break_time: boolean
    task_project_setting_view: boolean
    high_volume: boolean
    risk_member_report_view: boolean
    risk_team_report_view: boolean
    organizational_analysis_view: boolean
    task_project_editable: boolean
    task_labeling_status_view: boolean
  }
  function_enabled_state: {
    ai_work_assessment: boolean
    organizational_analysis: boolean
    risk_assessment: boolean
    self_management: boolean
    task_management: boolean
    task_management_approval: boolean
  }
  role_permission: RolePermission
  role_target_restriction: boolean
}

const initialState: Partial<LoginState> = {
  isLoading: true,
  isMasterLogin: false,
}

const loginSlice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    requestStart: (state) => {
      state.isLoading = true
      return state
    },
    createLoginSuccess: (state, action: PayloadAction<LoginResponse>) => {
      state.current_member_id = action.payload.member?.id
      state.company_id = action.payload.member?.company_id
      state.isLoading = false
      state.isMasterLogin = false
      state.accessToken = action.payload.token
      state.role = action.payload.member.role
      state.role_name = action.payload.member.role_name
      state.company_plan = action.payload.member.company_plan || ""
      state.limit_member_number = action.payload.member.limit_member_number
      state.feature_config = action.payload.member.feature_config
      state.function_enabled_state = action.payload.member.function_enabled_state
      state.role_permission = action.payload.member.role_permission
      state.role_target_restriction = action.payload.member.role_target_restriction
      return state
    },
    createMasterLoginSuccess: (state, action: PayloadAction<LoginResponse>) => {
      state.current_member_id = action.payload.member?.id
      state.company_id = action.payload.member?.company_id
      state.isLoading = false
      state.isMasterLogin = true
      state.accessToken = action.payload.token
      state.role = action.payload.member.role
      state.role_name = action.payload.member.role_name
      state.company_plan = action.payload.member.company_plan || ""
      state.limit_member_number = action.payload.member.limit_member_number
      state.feature_config = action.payload.member.feature_config
      state.function_enabled_state = action.payload.member.function_enabled_state
      state.role_permission = action.payload.member.role_permission
      state.role_target_restriction = action.payload.member.role_target_restriction
      return state
    },
    createLogoutSuccess: (state) => {
      state.current_member_id = undefined
      state.company_id = undefined
      state.isLoading = false
      if (state.isMasterLogin === false) {
        state.accessToken = undefined
      }
      state.company_plan = undefined
      state.limit_member_number = undefined
      state.role = []
      state.role_name = null
      return state
    },
    requestFailure: (state) => {
      state.isLoading = false
      return state
    },
    resetLoginState: (state) => {
      // eslint-disable-next-line no-param-reassign
      state = initialState
      return state
    },
  },
})

export const {
  requestStart,
  createLoginSuccess,
  createMasterLoginSuccess,
  createLogoutSuccess,
  requestFailure,
  resetLoginState,
} = loginSlice.actions

export const loginReducer = loginSlice.reducer

export const createLogin = (
  login: Login,
  callback: (data: LoginResponse) => void,
  errorCallback: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  postLogin(login)
    .then((result) => {
      if (result.token.length > 0) {
        dispatch(createLoginSuccess(result))
      }
      dispatch(resetFormErrors())
      callback(result)
    })
    .catch((error) => {
      dispatch(requestFailure())
      errorCallback()
      if (error.needResetToken) {
        dispatch(setErrors({
          status: error.status,
          needResetToken: false,
          errors: [{
            message: ['ログインに失敗しました。'],
            column_name: 'form',
          }],
        }))
      } else {
        dispatch(setErrors(error))
      }
    })
}

export const fetchMfaToken = (
  login: Login,
  callback: (data: LoginResponse) => void,
  failedCallback: () => void,
): AppThunk => async (dispatch) => {
  getMfaToken(login)
    .then((result) => {
      dispatch(createLoginSuccess(result))
      dispatch(resetFormErrors())
      callback(result)
    })
    .catch(() => {
      dispatch(requestFailure())
      failedCallback()
    })
}

export const fetchAccessToken = (
  callback: (data: LoginResponse) => void,
  failedCallback: () => void,
): AppThunk => async (dispatch) => {
  getAccessToken()
    .then((result) => {
      dispatch(createLoginSuccess(result))
      dispatch(resetFormErrors())
      callback(result)
    })
    .catch(() => {
      dispatch(requestFailure())
      failedCallback()
    })
}

export const sendLogout = (
  callback: () => void,
): AppThunk => async (dispatch) => {
  postLogout()
    .then(() => {
      dispatch(createLogoutSuccess())
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const masterLogin = (
  login: Login,
  callback: (data: Login) => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  postMasterLogin(login)
    .then((result) => {
      dispatch(createMasterLoginSuccess(result))
      callback(result)
    })
    .catch((error) => {
      dispatch(requestFailure())
      if (error.needResetToken) {
        dispatch(setErrors({
          status: error.status,
          needResetToken: false,
          errors: [{
            message: ['ログインに失敗しました。'],
            column_name: 'form',
          }],
        }))
      } else {
        dispatch(setErrors(error))
      }
    })
}

export const masterLoginManagement = (
  param: { member_id: number },
  callback: (data: Login) => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  postMasterLoginManagement(param)
    .then((result) => {
      dispatch(createMasterLoginSuccess(result))
      callback(result)
    })
    .catch(() => dispatch(requestFailure()))
}
